import * as React from 'react'
import {useEffect} from 'react'
import {Lang} from 'yougile-lang'
import {Hub, Tag, TimetrackerValue} from 'yougile-model'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {TimetrackingCtl} from '@extensions/sticker/controllers/timetracking-ctl'
import {Sticker} from '@extensions/sticker/sticker'
import {StickerItemData} from '../a-sticker-view'
import {HoursMinutesInput, makeHoursMinutesModel} from './hours-minutes-input'

interface Props {
  sticker: Tag
  task: Hub
  data: StickerItemData
  close: () => boolean | void
  complete: boolean
  disabledPlan?: boolean
  disabledWork?: boolean
}

export const TimetrackingEditInner = (props: Props) => {
  const {task, complete, close, sticker, data, disabledPlan, disabledWork} =
    props

  const planModel = makeHoursMinutesModel()
  const workModel = makeHoursMinutesModel()
  const val = task.stickers[sticker.id] as TimetrackerValue
  const [cur, setCur] = React.useState(val ?? {plan: 0, work: 0})

  const save = () => {
    const planValue = planModel?.getValue()?.floatHours
    const workValue = workModel?.getValue()?.floatHours
    const value: TimetrackerValue = {
      plan: cur.plan ?? planValue,
      work: cur.work ?? workValue
    }
    Sticker.pin(sticker, data.containerKey, task, value)
  }

  useEffect(() => {
    save()
    return save
  }, [
    planModel?.getValue()?.floatHours,
    workModel?.getValue()?.floatHours,
    cur.plan,
    cur.work
  ])

  const [state, setState] = React.useState({
    planEdit: false,
    workEdit: false
  })

  const warn = complete ? (
    <div className="sti-warn">{Lang.msg.ttFillToClose}:</div>
  ) : undefined

  const cancel = complete ? (
    <div className="sticker-item-details-cancel _link" onClick={close}>
      {Lang.msg.cancel}
    </div>
  ) : undefined

  const editPlan = () => setState(st => ({...st, planEdit: true}))
  const savePlan = () => {
    const v = planModel.getValue()
    if (v.isOk) {
      if (v.floatHours !== cur.plan) {
        Tracking.send(
          TrackingEvent.timetrackingSetTheEstimationValueOnTimetrackingSticker
        )
      }

      setCur(c => ({...c, plan: v.floatHours}))
      setState(st => ({...st, planEdit: false}))
    }
  }

  const editWork = () => setState(s => ({...s, workEdit: true}))
  const saveWork = () => {
    const v = workModel.getValue()
    if (v.isOk) {
      if (v.floatHours !== cur.work) {
        Tracking.send(
          TrackingEvent.timetrackingSetTheSpentTimeValueOnTimetrackingSticker
        )
      }

      setCur(c => ({...c, work: v.floatHours}))
      setState(st => ({...st, workEdit: false}))
    }
  }

  const chartStyle = {
    width:
      (cur.plan ? Math.min(100, Math.round((100 * cur.work) / cur.plan)) : 0) +
      '%'
  }

  const chartStyleOver = {
    width:
      (cur.plan
        ? Math.max(
            0,
            Math.min(100, Math.round(100 * ((cur.work - cur.plan) / cur.work)))
          )
        : 0) + '%'
  }

  const {h2s, estimation, spent} = TimetrackingCtl

  const chart = cur.plan ? (
    <div className="sti-chart">
      <div className="sti-chart-cnt">
        <div className="sti-chart-line" style={chartStyle} />
        <div className="sti-chart-line-over" style={chartStyleOver} />
      </div>
      <div className="sti-chart-desc">
        {h2s(cur.work, '?')} {Lang.msg.of} {h2s(cur.plan, '?')}
      </div>
    </div>
  ) : undefined

  const plan = (
    <div className="sti-desc flex">
      <span className="sti-desc-txt">{Lang.msg.ttEstimation}:</span>
      {state.planEdit ? (
        <HoursMinutesInput
          initial={cur.plan}
          model={planModel}
          save={savePlan}
          close={close}
          className="sti-hm"
        />
      ) : (
        <span className="sti-desc-num">{estimation(cur)}</span>
      )}
      {!disabledPlan && (
        <span
          className="sti-desc-btn _link ml-auto"
          onClick={state.planEdit ? savePlan : editPlan}
        >
          {state.planEdit ? Lang.msg.save : Lang.msg.change}
        </span>
      )}
    </div>
  )

  const work = (
    <div className="sti-desc flex">
      <span className="sti-desc-txt">{Lang.msg.ttSpent}:</span>
      {state.workEdit ? (
        <HoursMinutesInput
          initial={cur.work}
          save={saveWork}
          close={close}
          model={workModel}
          className="sti-hm"
        />
      ) : (
        <span className="sti-desc-num">{spent(cur)}</span>
      )}
      {!disabledWork && (
        <span
          className="sti-desc-btn _link ml-auto"
          onClick={state.workEdit ? saveWork : editWork}
        >
          {state.workEdit ? Lang.msg.save : Lang.msg.change}
        </span>
      )}
    </div>
  )

  return (
    <div className="sticker-item-list-body">
      <div className="sticker-item-details">
        {!cur.plan && warn}
        {plan}
        {!cur.work && warn}
        {work}
        {cancel}
        {chart}
      </div>
    </div>
  )
}
